// Utils
import { pg1MaxRowsWithChart, pg2MaxRowsWithChart, pg3MaxRowsWithChart, GICBenchmarkSymbol, GIF_CLASS_TYPES } from './constants';
import { COMPARISON_ITEM_TYPES } from '../constants';
import ComparisonItem from '@/types/comparison-item';
import BMOGIFItem from '@/types/bmo-gif/bmo-gif-item';
import BMOGIFScenarioData from '@/types/bmo-gif/bmo-gif-scenario-data';
import BMOGIFScenario from '@/types/bmo-gif/bmo-gif-scenario';
import BMOGIFPortfolio from '@/types/bmo-gif/bmo-gif-portfolio';
import BMOProposalData from '@/types/bmo-gif/bmo-proposal-data';

export const getAnnualData = (data: BMOGIFScenarioData[], purchaseDate: Date): BMOGIFScenarioData[] => {
  let withdrawalSum = 0;
  let depositSum = 0;
  return data
    .map((item, index) => {
      // Flat object so shallow copy is fine
      const copy = { ...item };
      if (copy.calendarMonth >= 1 && copy.calendarMonth <= 11) {
        withdrawalSum += copy.netWithdrawal;
        depositSum += copy.netDeposit;
      }
      if (copy.calendarMonth === 12) {
        copy.netWithdrawal += withdrawalSum;
        copy.netDeposit += depositSum;
        withdrawalSum = 0;
        depositSum = 0;
      }
      if (index === 0) {
        copy.date = new Date(purchaseDate);
        copy.netWithdrawal = 0;
        copy.netDeposit = 0;
      } else {
        const row = data[index + 1];
        if (row) {
          copy.portfolioValue = row.portfolioValue;
          copy.maturityGuarantee = row.maturityGuarantee;
          copy.deathBenefitGuarantee = row.deathBenefitGuarantee;
        }
      }
      return copy;
    })
    .filter((item, index) => index === 0 || item.calendarMonth === 12);
};

/**
 * Split proposal scenario into pages if data rows overflow
 *
 * @param {BMOGIFPortfolio} scenario GIF Portfolio
 * @return {BMOGIFScenarioData[][]} Array of bmo gif scenario data
 */
export const splitScenarioData = (scenario: BMOGIFScenario, portfolio: BMOGIFPortfolio, isAdverse?: boolean, scenarioNumber?: number): BMOProposalData[] => {
  const data = isAdverse ? getAnnualData(scenario.adverseData, scenario.purchaseDate) : getAnnualData(scenario.data, scenario.purchaseDate);

  // Max num of rows a scenario page can take
  const pg1MaxRows = 37;
  const pg2MaxRows = 40;
  const pg3MaxRows = 40;

  // Default Values: Range of rows to fit data + a chart
  let pageOneDataRange = [0, pg1MaxRowsWithChart];
  let pageTwoDataRange = [pg1MaxRows, pg1MaxRows + pg2MaxRowsWithChart];
  let pageThreeDataRange = [pg1MaxRows + pg2MaxRows, pg1MaxRows + pg2MaxRows + pg3MaxRowsWithChart];
  // 150 is an arbitrary number just to get slice up to last value in array, (we know max term is 99 years)
  const pageFourDataRange = [pg1MaxRows + pg2MaxRows + pg3MaxRows, 150];

  let splitData = [data.slice(pageOneDataRange[0], pageOneDataRange[1])];

  // If more data that can fit on page 1, create a second page and update page1 range
  if (data.length > pg1MaxRowsWithChart && data.length <= pg1MaxRows + pg2MaxRowsWithChart) {
    pageOneDataRange = [0, pg1MaxRows];

    splitData = [data.slice(pageOneDataRange[0], pageOneDataRange[1]), data.slice(pageTwoDataRange[0], pageTwoDataRange[1])];
  }

  // If more data that can fit on page 2, create a third page and update page1 and page 2 ranges
  if (data.length > pg1MaxRows + pg2MaxRowsWithChart && data.length <= pg1MaxRows + pg2MaxRows + pg3MaxRowsWithChart) {
    pageOneDataRange = [0, pg1MaxRows];
    pageTwoDataRange = [pg1MaxRows, pg1MaxRows + pg2MaxRows];

    splitData = [
      data.slice(pageOneDataRange[0], pageOneDataRange[1]),
      data.slice(pageTwoDataRange[0], pageTwoDataRange[1]),
      data.slice(pageThreeDataRange[0], pageThreeDataRange[1]),
    ];
  }

  // If more data that can fit on page 3, create a fourth page and update page1, page 2, and page 3 ranges
  if (data.length > pg1MaxRows + pg2MaxRows + pg3MaxRowsWithChart) {
    pageOneDataRange = [0, pg1MaxRows];
    pageTwoDataRange = [pg1MaxRows, pg1MaxRows + pg2MaxRows];
    pageThreeDataRange = [pg1MaxRows + pg2MaxRows, pg1MaxRows + pg2MaxRows + pg2MaxRows];

    splitData = [
      data.slice(pageOneDataRange[0], pageOneDataRange[1]),
      data.slice(pageTwoDataRange[0], pageTwoDataRange[1]),
      data.slice(pageThreeDataRange[0], pageThreeDataRange[1]),
      data.slice(pageFourDataRange[0], pageFourDataRange[1]),
    ];
  }

  return splitData.map((d, i) => ({
    scenario,
    pageData: d,
    scenarioNumber,
    scenarioPageIndex: i + 1,
    isAdverse,
    portfolio,
  }));
};

export const splitGifItemToPages = (item: BMOGIFItem, hideBaseScenario: boolean = false) => {
  const pages = [];
  if (!hideBaseScenario) {
    pages.push(...splitScenarioData(item.baseScenario, item.portfolio));
    pages.push(...splitScenarioData(item.baseScenario, item.portfolio, true));
  }

  for (let i = 0; i < item.scenarios.length; i++) {
    pages.push(...splitScenarioData(item.scenarios[i], item.portfolio, undefined, i + 1));
    pages.push(...splitScenarioData(item.scenarios[i], item.portfolio, true, i + 1));
  }

  return pages;
};

export const isGICBenchmark = (item: ComparisonItem) =>
  item.type === COMPARISON_ITEM_TYPES.PORTFOLIO && item.holdings.length === 1 && item.holdings[0].symbol && item.holdings[0].symbol === GICBenchmarkSymbol;

export const isPrestige = (classType?: string): boolean => classType === GIF_CLASS_TYPES.CLASS_A_PRESTIGE || classType === GIF_CLASS_TYPES.CLASS_F_PRESTIGE;
